import React from "react"
import { useForm, useFieldArray } from "react-hook-form"
import { SMLManager } from "../../../../types/smlManager"
import { TAcreateSmlManagers, TAupdateSmlManagers } from "../../../../services/managerAPI"
import { useEffect } from "react"

interface CreateManagerModalProps {
  manager: SMLManager | null
  isOpen: boolean
  onClose: () => void
}

const UpdateManagerModal: React.FC<CreateManagerModalProps> = ({ manager, isOpen, onClose }) => {
  type ManagerFormType = Omit<
    SMLManager,
    "companyNames" | "brand_accounts" | "products" | "date_range"
  > & {
    companyNames: string[]
    brand_accounts: string[]
    products: string[]
    date_range: {
      start: string
      end: string
    }
  }

  const { register, control, handleSubmit, reset } = useForm<ManagerFormType>({
    defaultValues: {
      ...manager,
      date_range: {
        start: manager?.date_range?.start
          ? new Date(manager.date_range.start).toISOString().split("T")[0]
          : "",
        end: manager?.date_range?.end
          ? new Date(manager.date_range.end).toISOString().split("T")[0]
          : "",
      },
    },
  })

  useEffect(() => {
    if (manager) {
      reset({
        ...manager,
        date_range: {
          start: manager?.date_range?.start
            ? new Date(manager.date_range.start).toISOString().split("T")[0]
            : "",
          end: manager?.date_range?.end
            ? new Date(manager.date_range.end).toISOString().split("T")[0]
            : "",
        },
      })
    }
  }, [manager, reset])

  const {
    fields: tagFields,
    append: appendTag,
    remove: removeTag,
  } = useFieldArray({
    control,
    name: "instagram.tags",
  })

  const {
    fields: hashtagFields,
    append: appendHashtag,
    remove: removeHashtag,
  } = useFieldArray({
    control,
    name: "instagram.hashtags",
  })

  const {
    fields: tiktokFields,
    append: appendTikTok,
    remove: removeTikTok,
  } = useFieldArray({
    control,
    name: "tiktok.search",
  })

  const {
    fields: linkedInFields,
    append: appendLinkedIn,
    remove: removeLinkedIn,
  } = useFieldArray({
    control,
    name: "linkedIn.search",
  })

  const {
    fields: twitterFields,
    append: appendTwitter,
    remove: removeTwitter,
  } = useFieldArray({
    control,
    name: "twitter.search",
  })

  const {
    fields: youtubeFields,
    append: appendYoutube,
    remove: removeYoutube,
  } = useFieldArray({
    control,
    name: "youtube.search",
  })

  const {
    fields: companyFields,
    append: appendCompany,
    remove: removeCompany,
  } = useFieldArray({
    control,
    name: "companyNames" as any,
  })

  const {
    fields: brandFields,
    append: appendBrand,
    remove: removeBrand,
  } = useFieldArray({
    control,
    name: "brand_accounts" as any,
  })
  const {
    fields: productFields,
    append: appendProduct,
    remove: removeProduct,
  } = useFieldArray({
    control,
    name: "products" as any,
  })

  const onSubmit = async (data: ManagerFormType) => {
    const payload = {
      ...data,
      date_range: {
        start: new Date(data.date_range.start),
        end: new Date(data.date_range.end),
      },
    }
    await TAupdateSmlManagers(data._id, payload)
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Yeni Manager Oluştur</h2>
          <button
            className="text-gray-500 hover:text-red-600 font-bold text-2xl"
            type="button"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Instagram Tags</h3>
          {tagFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`instagram.tags.${index}.keyword`)}
                placeholder="Keyword"
                className="border rounded px-2 py-1 w-1/3"
              />
              <input
                {...register(`instagram.tags.${index}.cursor`)}
                placeholder="Cursor"
                className="border rounded px-2 py-1 w-1/3"
              />
              <select
                {...register(`instagram.tags.${index}.state`)}
                className="border rounded px-2 py-1"
              >
                <option value="ready">Ready</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
              </select>
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendTag({ keyword: "", cursor: "", state: "ready" })}
            className="text-blue-600 hover:underline"
          >
            + Yeni Tag Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Instagram Hashtags</h3>
          {hashtagFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`instagram.hashtags.${index}.keyword`)}
                placeholder="Keyword"
                className="border rounded px-2 py-1 w-1/4"
              />
              <input
                {...register(`instagram.hashtags.${index}.cursor`)}
                placeholder="Cursor"
                className="border rounded px-2 py-1 w-1/4"
              />
              <input
                {...register(`instagram.hashtags.${index}.page`)}
                placeholder="Page"
                className="border rounded px-2 py-1 w-1/6"
              />
              <select
                {...register(`instagram.hashtags.${index}.state`)}
                className="border rounded px-2 py-1"
              >
                <option value="ready">Ready</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
              </select>
              <button
                type="button"
                onClick={() => removeHashtag(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendHashtag({ keyword: "", cursor: "", page: "", state: "ready" })}
            className="text-blue-600 hover:underline"
          >
            + Yeni Hashtag Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">TikTok Searchs</h3>
          {tiktokFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`tiktok.search.${index}.keyword`)}
                placeholder="Keyword"
                className="border rounded px-2 py-1 w-1/4"
              />
              <input
                {...register(`tiktok.search.${index}.cursor`)}
                placeholder="Cursor"
                className="border rounded px-2 py-1 w-1/4"
              />
              <select
                {...register(`tiktok.search.${index}.state`)}
                className="border rounded px-2 py-1"
              >
                <option value="ready">Ready</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
              </select>
              <button
                type="button"
                onClick={() => removeTikTok(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendTikTok({ keyword: "", cursor: "", state: "ready" })}
            className="text-blue-600 hover:underline"
          >
            + Yeni Search Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">LinkedIn Search</h3>
          {linkedInFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`linkedIn.search.${index}.keyword`)}
                placeholder="Keyword"
                className="border rounded px-2 py-1 w-1/3"
              />
              <select
                {...register(`linkedIn.search.${index}.state`)}
                className="border rounded px-2 py-1"
              >
                <option value="ready">Ready</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
              </select>
              <button
                type="button"
                onClick={() => removeLinkedIn(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendLinkedIn({ keyword: "", state: "ready" })}
            className="text-blue-600 hover:underline"
          >
            + Yeni Search Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Twitter Search</h3>
          {twitterFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`twitter.search.${index}.keyword`)}
                placeholder="Keyword"
                className="border rounded px-2 py-1 w-[22%]"
              />
              <input
                {...register(`twitter.search.${index}.cursor`)}
                placeholder="Cursor"
                className="border rounded px-2 py-1 w-[22%]"
              />
              <input
                type="date"
                {...register(`twitter.search.${index}.start_date`)}
                className="border rounded px-2 py-1 w-[22%]"
              />
              <input
                type="date"
                {...register(`twitter.search.${index}.end_date`)}
                className="border rounded px-2 py-1 w-[22%]"
              />
              <select
                {...register(`twitter.search.${index}.state`)}
                className="border rounded px-2 py-1"
              >
                <option value="ready">Ready</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
              </select>
              <button
                type="button"
                onClick={() => removeTwitter(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              appendTwitter({
                keyword: "",
                cursor: "",
                start_date: "",
                end_date: "",
                state: "ready",
              })
            }
            className="text-blue-600 hover:underline"
          >
            + Yeni Search Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">YouTube Search</h3>
          {youtubeFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`youtube.search.${index}.keyword`)}
                placeholder="Keyword"
                className="border rounded px-2 py-1 w-[22%]"
              />
              <input
                {...register(`youtube.search.${index}.cursor`)}
                placeholder="Cursor"
                className="border rounded px-2 py-1 w-[22%]"
              />
              <input
                type="date"
                {...register(`youtube.search.${index}.publishedAfter`)}
                className="border rounded px-2 py-1 w-[22%]"
              />
              <input
                type="date"
                {...register(`youtube.search.${index}.publishedBefore`)}
                className="border rounded px-2 py-1 w-[22%]"
              />
              <select
                {...register(`youtube.search.${index}.state`)}
                className="border rounded px-2 py-1"
              >
                <option value="ready">Ready</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
              </select>
              <button
                type="button"
                onClick={() => removeYoutube(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              appendYoutube({
                keyword: "",
                cursor: "",
                publishedAfter: "",
                publishedBefore: "",
                state: "ready",
              })
            }
            className="text-blue-600 hover:underline"
          >
            + Yeni Search Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Şirket İsimleri (companyNames)</h3>
          {companyFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`companyNames.${index}`)}
                placeholder="Şirket Adı"
                className="border rounded px-2 py-1 w-full"
              />
              <button
                type="button"
                onClick={() => removeCompany(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendCompany("")}
            className="text-blue-600 hover:underline"
          >
            + Şirket Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Marka Hesapları (brand_accounts)</h3>
          {brandFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`brand_accounts.${index}`)}
                placeholder="Marka Hesabı"
                className="border rounded px-2 py-1 w-full"
              />
              <button
                type="button"
                onClick={() => removeBrand(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendBrand("")}
            className="text-blue-600 hover:underline"
          >
            + Marka Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Ürünler (products)</h3>
          {productFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 mb-2">
              <input
                {...register(`products.${index}`)}
                placeholder="Ürün Adı"
                className="border rounded px-2 py-1 w-full"
              />
              <button
                type="button"
                onClick={() => removeProduct(index)}
                className="text-red-500 hover:underline"
              >
                Sil
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendProduct("")}
            className="text-blue-600 hover:underline"
          >
            + Ürün Ekle
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Tarih Aralığı (date_range)</h3>
          <div className="flex items-center gap-4">
            <div className="flex flex-col w-1/2">
              <label className="text-sm text-gray-600 mb-1">Başlangıç</label>
              <input
                type="date"
                {...register("date_range.start")}
                className="border rounded px-2 py-1"
              />
            </div>
            <div className="flex flex-col w-1/2">
              <label className="text-sm text-gray-600 mb-1">Bitiş</label>
              <input
                type="date"
                {...register("date_range.end")}
                className="border rounded px-2 py-1"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-all"
        >
          Güncelle
        </button>
      </form>
    </div>
  )
}

export default UpdateManagerModal
